import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import {
  BookCheck,
  Building2,
  CreditCard,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Settings,
  Users,
} from 'lucide-react'
import { Avatar } from './Avatar'
import { ThemeToggle } from './ThemeToggle'
import { avatarColors } from '../lib/avatar'
import { useSession } from '../lib/session'

const teachingNav = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/groups', label: 'Groups', icon: Users, end: false },
  { to: '/coverage', label: 'Coverage', icon: BookCheck, end: false },
]

/** Admin-only. Ordered the way the API's dependencies run: programmes and
 *  structure exist before a roster import can resolve them by name. */
const schoolNav = [
  { to: '/school/roster', label: 'Roster', icon: ListChecks, end: false },
  { to: '/school/structure', label: 'Structure', icon: Building2, end: false },
  { to: '/school/settings', label: 'Settings', icon: Settings, end: false },
  { to: '/school/billing', label: 'Billing', icon: CreditCard, end: false },
]

function Brand({ role }: { role: string }) {
  return (
    <div className="flex items-center gap-3 px-2">
      <div className="brand-mark h-10 w-10" aria-hidden="true" />
      <div>
        <p className="text-base font-semibold leading-tight tracking-tight text-ink">
          Modulus
        </p>
        <p className="text-xs font-medium capitalize text-muted">{role}</p>
      </div>
    </div>
  )
}

function NavItems({
  items,
  onNavigate,
}: {
  items: typeof teachingNav
  onNavigate?: () => void
}) {
  return (
    <nav className="flex gap-1 md:flex-col">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex flex-1 items-center gap-3 rounded-2xl px-3.5 py-2.5 text-sm font-semibold transition md:flex-none ${
              isActive
                ? 'bg-accent-wash text-accent shadow-sm'
                : 'text-muted hover:bg-surface hover:text-ink'
            }`
          }
        >
          <Icon size={18} />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  )
}

function NavGroupLabel({ children }: { children: ReactNode }) {
  return (
    <p className="px-3.5 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-faint">
      {children}
    </p>
  )
}

export function Layout({ children }: { children: ReactNode }) {
  const { profile, staff, signOut, isAdmin } = useSession()
  const name = profile?.display_name?.trim() || profile?.email || 'Signed in'
  const role = staff?.role ?? 'staff'

  return (
    <div className="relative min-h-screen">
      {/* Ambient app-style background */}
      <div className="pointer-events-none fixed inset-0 -z-10 bg-bg" />
      <div className="pointer-events-none fixed -left-40 -top-40 -z-10 h-[28rem] w-[28rem] rounded-full bg-accent-wash blur-3xl" />
      <div className="pointer-events-none fixed -right-32 top-1/3 -z-10 h-[26rem] w-[26rem] rounded-full bg-accent-wash blur-3xl" />
      <div className="pointer-events-none fixed bottom-0 left-1/3 -z-10 h-80 w-80 rounded-full bg-accent-wash blur-3xl" />

      <div className="mx-auto flex min-h-screen max-w-[1500px] flex-col gap-4 p-4 md:flex-row md:gap-6 md:p-6">
        {/* Sidebar */}
        <aside className="glass-strong z-10 flex shrink-0 flex-col gap-6 rounded-3xl p-4 md:sticky md:top-6 md:h-[calc(100vh-3rem)] md:w-64">
          <div className="hidden pt-2 md:block">
            <Brand role={role} />
          </div>

          <div className="flex items-center justify-between md:hidden">
            <Brand role={role} />
          </div>

          <div>
            <NavItems items={teachingNav} />

            {isAdmin && (
              <>
                <NavGroupLabel>School</NavGroupLabel>
                <NavItems items={schoolNav} />
              </>
            )}
          </div>

          <div className="mt-auto hidden md:block">
            <div className="mb-3">
              <ThemeToggle />
            </div>
            <div className="flex items-center gap-3 rounded-2xl bg-surface p-3">
              <Avatar name={name} colors={avatarColors(profile?.id ?? name)} size={40} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-ink">{name}</p>
                <p className="truncate text-xs text-muted">{staff?.school.name}</p>
              </div>
              <button
                onClick={() => void signOut()}
                title="Sign out"
                aria-label="Sign out"
                className="rounded-xl p-2 text-faint transition hover:bg-surface hover:text-ink"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0 flex-1 pb-10">{children}</main>
      </div>
    </div>
  )
}
